import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { homeInsurancePage, companyInfo } from '../mock';
import { CheckCircle2, Phone, MessageCircle, Home, Building, Key, AlertTriangle, Info } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const HomeInsurance = () => {
  const { language } = useLanguage();
  const content = homeInsurancePage[language];

  const whatsappUrl = `https://wa.me/${companyInfo.phoneWhatsApp}?text=${encodeURIComponent(content.whatsappMessage)}`;

  const iconMap = {
    "Homeowners": Home,
    "Condo Insurance": Building,
    "Renters Insurance": Key,
    "Propietarios": Home,
    "Seguro de Condominio": Building,
    "Seguro para Inquilinos": Key
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-700 to-green-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {content.title}
            </h1>
            <p className="text-xl md:text-2xl text-green-100 mb-8">
              {content.subtitle}
            </p>
            <p className="text-lg text-green-50">
              {content.description}
            </p>
          </div>
        </div>
      </section>

      {/* Important Coverage Information */}
      <section className="py-16 bg-yellow-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <AlertTriangle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {content.importantCoverageNotes.title}
              </h2>
            </div>

            <div className="space-y-6">
              {/* Dwelling Coverage */}
              <div className="bg-white border-l-4 border-green-500 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Home className="w-6 h-6 text-green-600" />
                  {content.importantCoverageNotes.dwelling.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {content.importantCoverageNotes.dwelling.description}
                </p>
              </div>

              {/* Flood Insurance */}
              <div className="bg-white border-l-4 border-blue-500 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 text-blue-600" />
                  {content.importantCoverageNotes.flood.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {content.importantCoverageNotes.flood.description}
                </p>
              </div>

              {/* Wind & Hail Coverage */}
              <div className="bg-white border-l-4 border-orange-500 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                  {content.importantCoverageNotes.windHail.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {content.importantCoverageNotes.windHail.description}
                </p>
              </div>

              {/* Note */}
              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-lg">
                <p className="text-sm text-blue-900 flex items-start gap-2">
                  <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>Note:</strong> {content.importantCoverageNotes.note}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Types */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
            {content.coverageTypes.title}
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {content.coverageTypes.options.map((option, index) => {
              const Icon = iconMap[option.type] || Home;
              return (
                <Card key={index} className="text-center border-none shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-green-700 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {option.type}
                    </h3>
                    <p className="text-gray-600">
                      {option.desc}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coverage Examples */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {language === 'en' ? 'Coverage Examples' : 'Ejemplos de Cobertura'}
            </h2>
            <div className="grid gap-4">
              {content.coverageExamples.map((example, index) => (
                <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <p className="text-lg text-gray-700">{example}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What to Prepare */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {content.whatToPrepare.title}
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
              <ul className="space-y-3">
                {content.whatToPrepare.items.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-blue-600 font-bold">•</span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {content.faq.title}
              </h2>
              <p className="text-lg text-gray-600">
                {content.faq.subtitle}
              </p>
            </div>
            <div className="space-y-6">
              {content.faq.items.map((item, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {item.question}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {language === 'en' ? 'Get a Quote' : 'Obtener Cotización'}
            </h2>
            <p className="text-xl text-red-100 mb-8">
              {language === 'en' 
                ? 'Contact us today for a personalized home insurance quote' 
                : 'Contáctenos hoy para una cotización personalizada de seguro de hogar'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white text-red-700 px-8 py-4 rounded-full text-lg font-semibold hover:bg-red-50 transition-all shadow-lg focus:outline-none focus:ring-4 focus:ring-red-300"
                aria-label={language === 'en' ? 'Get quote via WhatsApp' : 'Obtener cotización por WhatsApp'}
              >
                <MessageCircle className="w-6 h-6" />
                {language === 'en' ? 'Quote via WhatsApp' : 'Cotizar por WhatsApp'}
              </a>
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 bg-red-800 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-red-900 transition-all shadow-lg focus:outline-none focus:ring-4 focus:ring-red-400"
                aria-label={language === 'en' ? `Call ${companyInfo.phone}` : `Llamar ${companyInfo.phone}`}
              >
                <Phone className="w-6 h-6" />
                {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomeInsurance;